import {Divider} from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Drawer from '@mui/material/Drawer';
import React from 'react';
import {SupportTicketResolutionEnum} from '../../../../model/support/support-ticket-resolution.enum';
import {SupportTicketStatusEnum} from '../../../../model/support/support-ticket-status.enum';
import {SupportTicketTypeEnum} from '../../../../model/support/support-ticket-type.enum';
import {SupportTicketModel} from '../../../../model/support/support-ticket.model';
import {useSupportTicketDrawerState} from '../state/support-ticket-drawer-state';
import {SupportTicketDrawerAnalysis} from './SupportTicketDrawerAnalysis';
import {SupportTicketDrawerClassification} from './SupportTicketDrawerClassification';
import {SupportTicketDrawerCustomerResolution} from './SupportTicketDrawerCustomerResolution';
import {SupportTicketDrawerFixedScheduled} from './SupportTicketDrawerFixedScheduled';
import {SupportTicketDrawerHeader} from './SupportTicketDrawerHeader';
import {SupportTicketDrawerResolution} from './SupportTicketDrawerResolution';
import {SupportTicketDrawerTicketDescription} from './SupportTicketDrawerTicketDescription';

type Props = {
  ticket: SupportTicketModel
  isDrawerOpen: boolean
  refreshTickets: () => Promise<void>
  onDrawerClosed: () => void
  translate: (key: string) => string
  translateLocal: (key: string) => string
}

export const SupportTicketDrawer = ({isDrawerOpen, onDrawerClosed, ticket, refreshTickets, translate, translateLocal}: Props) => {
  const state = useSupportTicketDrawerState(ticket, refreshTickets)

  const buildProceedButton = () => {
    return <Button
      type="submit"
      variant="contained"
      color="primary"
      onClick={state.onProcessedPressed}
      sx={{ flex: 1 }}
    >
      {translateLocal('ticket.drawer.button.proceed')}
    </Button>
  }

  const buildUpdateButton = () => {
    return <Button
      type="submit"
      variant="contained"
      color="primary"
      disabled={state.ticket.classifiedType === SupportTicketTypeEnum.IMPROVEMENT ? !state.isAnalyzeButtonEnabled : !state.isResolveButtonEnabled}
      onClick={async () => {
        if((state.ticket.status === SupportTicketStatusEnum.PROCESSING || state.ticket.status === SupportTicketStatusEnum.ANALYSIS_WAITING_CUSTOMER_APPROVAL) && state.ticket.classifiedType === SupportTicketTypeEnum.IMPROVEMENT) {
          await state.analyzeTicket();
        } else {
          await state.resolveTicket()
        }
      }}
      sx={{ flex: 1 }}
    >
      {translateLocal('ticket.drawer.button.update')}
    </Button>
  }

  const buildMarkAsFixedButton = () => {
    if(ticket.resolution === SupportTicketResolutionEnum.FIX_SCHEDULED && ticket.status === SupportTicketStatusEnum.IN_PROGRESS) {
      return <Button
        type="submit"
        variant="contained"
        color="primary"
        disabled={state.isDirtyState}
        onClick={state.markTicketAsFixed}
        sx={{ flex: 1 }}
      >
        {translateLocal('ticket.drawer.button.markFixed')}
      </Button>
    }

    return <></>
  }

  const buildSaveButton = () => {
    if(ticket.status === SupportTicketStatusEnum.PENDING) return buildProceedButton()

    return buildUpdateButton()
  }

  const buildButtons = () => <Box mt="auto" pt="8px" display="flex" gap={2}>
    <Button
      variant="outlined"
      onClick={onDrawerClosed}
      sx={{ flex: 1 }}
    >
      {translate('common.cancel.title')}
    </Button>
    {buildMarkAsFixedButton()}
    {buildSaveButton()}
  </Box>

  return <Drawer
    anchor="right"
    open={isDrawerOpen}
    onClose={onDrawerClosed}
    PaperProps={{
      sx: {
        width: '50%',
        marginTop: '64px',
        height: `calc(100% - 64px)`,
        position: 'fixed',
        right: 0,
        padding: 3,
        overflowY: 'auto',
      }
    }}
  >
    <SupportTicketDrawerHeader
      ticket={state.ticket}
      translate={translate}
    />
    <Divider sx={{my: 2}}/>
    <SupportTicketDrawerTicketDescription
      ticket={state.ticket}
      translate={translate}
      translateLocal={translateLocal}
    />
    {
      state.ticket.status !== SupportTicketStatusEnum.PENDING
        ? <Divider sx={{my: 2}}/>
        : <></>
    }
    {
      state.ticket.status !== SupportTicketStatusEnum.PENDING
        ? <SupportTicketDrawerClassification
          ticket={state.ticket}
          setTicket={state.setTicket}
          translate={translate}
          isEditDisabled={state.ticket.status !== SupportTicketStatusEnum.PROCESSING}
          translateLocal={translateLocal}
        /> : <></>
    }
    {
      state.ticket.classifiedType === 'IMPROVEMENT'
        ? <SupportTicketDrawerAnalysis
          ticket={state.ticket}
          setTicket={state.setTicket}
          isEditDisabled={state.ticket.status !== SupportTicketStatusEnum.PROCESSING && state.ticket.status !== SupportTicketStatusEnum.ANALYSIS_WAITING_CUSTOMER_APPROVAL}
          translate={translate}
          translateLocal={translateLocal}
        /> : <></>
    }
    {
      state.ticket.classifiedType === 'ERROR' || state.ticket.analyzedCustomerApproved
        ? <SupportTicketDrawerResolution
          isDisabled={state.ticket.status === 'CLOSED'}
          ticket={state.ticket}
          setTicket={state.setTicket}
          translate={translate}
          translateLocal={translateLocal}
        /> : <></>
    }
    {
      state.ticket.resolution !== SupportTicketResolutionEnum.FIX_SCHEDULED
        ? <></>
        : <SupportTicketDrawerFixedScheduled
          isDisabled={state.ticket.status === 'CLOSED'}
          ticket={state.ticket}
          setTicket={state.setTicket}
          translate={translate}
          translateLocal={translateLocal}
        />
    }
    {
      state.ticket.status === SupportTicketStatusEnum.REOPENED || state.ticket.status === SupportTicketStatusEnum.CLOSED
        ? <SupportTicketDrawerCustomerResolution
          ticket={state.ticket}
          translate={translate}
          translateLocal={translateLocal}
        /> : <></>
    }
    {state.ticket.status === 'CLOSED' ? <></> : buildButtons()}
  </Drawer>
}